import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'

import { authApi } from '../../services/auth'
import type { User } from '../../types'
import { UserRoleEnum } from '../../types'

interface AuthState {
    user: User | null
    token: string | null
    activeRole: UserRoleEnum | null
}

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('authToken'),
    activeRole: localStorage.getItem('activeRole') as UserRoleEnum | null,
}

export const ALLOWED_ROLES: UserRoleEnum[] = [
    UserRoleEnum.DISPATCHER,
    UserRoleEnum.TENANT_ADMIN,
    UserRoleEnum.EXECUTIVES,
]

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUser(state, action: PayloadAction<User>) {
            const roles: UserRoleEnum[] = action.payload.roles || []
            const filteredRoles = roles.filter((role) =>
                ALLOWED_ROLES.includes(role),
            )

            if (filteredRoles && filteredRoles.length === 1) {
                state.activeRole = filteredRoles[0]
                localStorage.setItem('activeRole', filteredRoles[0])
            }

            state.user = action.payload
        },
        setActiveRole(state, action: PayloadAction<UserRoleEnum>) {
            state.activeRole = action.payload
            localStorage.setItem('activeRole', action.payload)
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            authApi.endpoints.login.matchFulfilled,
            (state, { payload }) => {
                if (payload.data) {
                    state.token = payload.data.token
                    localStorage.setItem('authToken', payload.data.token)
                }
            },
        )
        builder.addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
            state.user = null
            state.token = null
            state.activeRole = null
            localStorage.removeItem('authToken')
            localStorage.removeItem('activeRole')
        })
        builder.addMatcher(
            authApi.endpoints.getMe.matchFulfilled,
            (state, { payload }) => {
                if (payload.data) {
                    state.user = payload.data
                }
            },
        )

        builder.addMatcher(authApi.endpoints.getMe.matchRejected, (state) => {
            state.user = null
            state.token = null
            state.activeRole = null
            localStorage.removeItem('authToken')
            localStorage.removeItem('activeRole')
        })

        builder.addMatcher(
            authApi.endpoints.updateMe.matchFulfilled,
            (state, { payload }) => {
                if (payload.data) {
                    state.user = payload.data
                }
            },
        )
    },
})

export const { setUser, setActiveRole } = authSlice.actions
export const getUser = (state: RootState) => state.auth.user
export default authSlice.reducer
